import { Dashboard } from "components/Layout";
import React, { useEffect, useState, useCallback } from "react";
import { RouteComponentProps } from "react-router-dom";
import UserGroups from "components/OrganizationAdmin/Groups/Groups";
import api from "api/api";
import { compose } from "ramda";
import StoreProvider from "components/StoreProvider/StoreProvider";
import { OrganizationType } from "store/modules/organizations/organizations";
import queryString from "query-string";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { GroupType } from "./types";
import { useFormatMessage } from "components/IntlProvider";

const { ROUTES } = RouterProvider;

export const defaultLimit = 20;

const { connect, selectors } = StoreProvider;

type GroupsListResponse = {
  count: number;
  results: GroupType[];
};

interface Props extends RouteComponentProps {
  organization: OrganizationType;
}

const UsersGroupPage = ({ location, organization, history }: Props) => {
  const [organizationGroups, setOrganizationGroups] =
    useState<GroupsListResponse>({
      count: 0,
      results: [],
    });

  const [isLoaded, setIsLoaded] = useState(false);
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  const getGroups = () => {
    return api.resources.organization.groups
      .getGroups({
        organizationCodename: organization.codename,
        params: {
          limit: (parsedSearch.limit as string) || defaultLimit,
          offset: (parsedSearch.offset as string) || 0,
          search: (parsedSearch.search as string) || undefined,
          ordering: (parsedSearch.ordering as string) || undefined,
        },
      })
      .then(({ data }: { data: GroupsListResponse }) => {
        setOrganizationGroups({ ...data });
      })
      .finally(() => {
        setIsLoaded(true);
      });
  };

  const getPageNumber = useCallback(() => {
    if (parsedSearch.offset && !Array.isArray(parsedSearch.offset)) {
      return Math.floor(parseInt(parsedSearch.offset, 10) / defaultLimit + 1);
    }

    return 0;
  }, [parsedSearch]);

  useEffect(() => {
    if (location.search) {
      getGroups();
    }
    // eslint-disable-next-line
  }, [location.search]);

  const sortColumns = (colName: string) => {
    const params = { ...parsedSearch, ordering: colName };

    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.GROUPS}?${queryString.stringify(params)}`
    );
  };

  return (
    <Dashboard title={t("dashboard.menu.admin", "Admin")} location={location}>
      <UserGroups
        isLoaded={isLoaded}
        groups={organizationGroups.results}
        count={organizationGroups.count}
        organization={organization.codename}
        refetchGroups={getGroups}
        currentPage={getPageNumber()}
        orderBy={parsedSearch.ordering as string}
        sortColumns={sortColumns}
      />
    </Dashboard>
  );
};

export default compose(
  connect((state) => ({
    organization: selectors.authSelectors.userOrganization(state),
    isAdmin: selectors.authSelectors.isOrganizationAdmin(state),
  }))
)(UsersGroupPage);
